import { empty, intval, strval, in_array, PhpArray, phpCopy, array_filter, array_values, sort, SORT_STRING, implode } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { array, values, isArray, slot, type Arr } from './access';
import { catalogId } from './catalog';
import { skillRollModifierLayerListSignatureSegment, type SkillListDeps } from './functions';
import sourceCatalogs from './source-catalogs.json';
import { strtoupper, trim } from '../../php-compat';
import { floatval } from '../../php-compat';
import { setPath } from './access';
export interface LuckSkillDeps extends SkillListDeps {
    layer_value(row: Arr, name: string): unknown;
    layer_entries(row: Arr, name: string): Arr;
    layer_has(row: Arr, name: string): boolean;
    layer_list_entry_uuid(entry: unknown): string;
    creature_payload_statuses(row: Arr, resolver?: ((...args: unknown[]) => unknown) | null): Arr;
    item_field(row: unknown, name: string): unknown;
    passive_ability_entry_is_active(entry: Arr): boolean;
    ft_entry_restore_item_arr(entry: Arr): Arr;
    ft_layer_entry_needs_resolve(entry: unknown): boolean;
    ft_resolve_layer_entry(entry: unknown, caster: Arr): unknown;
    contains_trigger_key(ability: Arr): boolean;
    ft_normalize_layer_entries_to_distinct_uuids(entries: Arr): Arr;
    v_status_id_of(target: Arr): unknown;
    creature_statuses(target: Arr, side: string): Arr;
    has_status_effect(target: Arr, id: string): boolean;
    build_caster_skills(caster: PhpRef<Arr>): void;
    caster_skill_entry(caster: Arr, variable: string): Arr | null;
    get_skill_tier_from_sp(points: number): Arr;
    'SkillTier::getAll'(): Arr;
    find_highest_average_roll_value(statuses: Arr, rollType: string): Arr | null;
    roll_dice(dice: unknown, die: unknown, mod: unknown): number;
}
/** PHP: apply_skill_roll_assistance_and_retracting (includes/functions/janus/core/luck_and_skill_roll_helpers.php:338) */
export function applySkillRollAssistanceAndRetracting(roll: PhpRef<Arr>, caster: Arr, env: PortEnv<LuckSkillDeps>): Arr {
    const assist = catalogId('RollType', 'skill_roll_assistance_roll', env);
    const retract = catalogId('RollType', 'skill_roll_retracting_roll', env);
    const output: (Arr | null)[] = [];
    for (const [id, sign] of [[assist, 1], [retract, -1]] as const) {
        const highest = env.deps.find_highest_average_roll_value(env.deps.creature_payload_statuses(caster), id);
        let selected: Arr | null = null;
        if (!empty(highest)) {
            selected = phpCopy((slot(highest, 'status_arr') ?? highest) as Arr);
            const result = env.deps.roll_dice(env.deps.layer_value(selected, 'dice') ?? 1,
                env.deps.layer_value(selected, 'die') ?? 4, env.deps.layer_value(selected, 'mod') ?? 0);
            const before = slot(roll.value, 'item_layer_types', 'roll_mod');
            setPath(roll.value, ['item_layer_types', 'roll_mod'], Number(floatval(before)) + sign * result);
            setPath(selected, ['roll_result', 0], result);
        }
        output.push(selected);
    }
    return output;
}
/** PHP: get_skill_sp_progress_to_next_tier (includes/functions/janus/core/luck_and_skill_roll_helpers.php:409) */
export function getSkillSpProgressToNextTier(points: number, env: PortEnv<LuckSkillDeps>): Arr {
    points = Math.max(0, points);
    const info = env.deps.get_skill_tier_from_sp(points), level = Number(intval(slot(info, 'level')));
    const byLevel = new Map(sourceCatalogs.SkillTier.map(row => [row.skill_tier_level, row]));
    const maximum = Math.max(...byLevel.keys());
    const current = Number(intval(byLevel.get(level)?.sp_required ?? 0));
    if (level >= maximum) return { at_max: true, pct: 100, current_req: current, next_req: null };
    const next = Number(intval(byLevel.get(level + 1)?.sp_required ?? 0)), span = next - current;
    if (span <= 0) return { at_max: true, pct: 100, current_req: current, next_req: null };
    return { at_max: false, pct: Math.max(0, Math.min(100, Math.floor(100 * (Math.min(points, next) - current) / span))),
        current_req: current, next_req: next };
}
function listContains(list: unknown, id: string, env: PortEnv<LuckSkillDeps>): boolean {
    if (!isArray(list)) return false;
    for (const entry of values(list)) {
        const resolved = isArray(entry) ? slot(entry, 0) ?? env.deps.layer_list_entry_uuid(entry) : entry;
        if (typeof resolved === 'string' && resolved === id) return true;
    }
    return false;
}
/** PHP: {closure@853:3350} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:853) */
export function closure853ListContains(list: unknown, id: string, env: PortEnv<LuckSkillDeps>): boolean { return listContains(list, id, env); }
/** PHP: {closure@922:3585} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:922) */
export function closure922ListContains(list: unknown, id: string, env: PortEnv<LuckSkillDeps>): boolean { return listContains(list, id, env); }
/** PHP: {closure@1018:3940} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1018) */
export function closure1018ListContains(list: unknown, id: string, env: PortEnv<LuckSkillDeps>): boolean { return listContains(list, id, env); }
/** PHP: {closure@1144:4311} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1144) */
export function closure1144TargetHasStatus(target: Arr | null, id: string, vitalityIds: Arr, env: PortEnv<LuckSkillDeps>): boolean {
    if (id === '' || !isArray(target)) return false;
    if (in_array(id, vitalityIds, true) && env.deps.v_status_id_of(target) === id) return true;
    return !empty(env.deps.creature_statuses(target, 'target')) && env.deps.has_status_effect(target, id);
}
/** PHP: {closure@1227:4580} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1227) */
export function closure1227ModTokens(entries: Arr, env: PortEnv<Pick<LuckSkillDeps, 'check_uuid'> & { skill_roll_mod_skill_and_modifier(entry: unknown): Arr }>): string {
    const mods: string[] = [];
    for (const entry of values(entries)) {
        const sm = env.deps.skill_roll_mod_skill_and_modifier(entry);
        if (env.deps.check_uuid(slot(sm, 'skill'))) mods.push(strval(slot(sm, 'skill')) + ':' + strval(slot(sm, 'mod')));
    }
    return implode(',', sort(mods, SORT_STRING));
}
/** PHP: janus_status_tick_forced_extreme_roll_type (includes/functions/janus/core/luck_and_skill_roll_helpers.php:780) */
export function janusStatusTickForcedExtremeRollType(status: Arr, env: PortEnv<LuckSkillDeps>): string {
    const dot = catalogId('RollType', 'damage_over_time_roll', env);
    return (env.deps.layer_value(status, 'roll_type') ?? '') === dot ? dot : catalogId('RollType', 'damage_roll', env);
}
/** PHP: janus_inject_status_forced_roll_extreme_into_caster_copy (includes/functions/janus/core/luck_and_skill_roll_helpers.php:801) */
export function janusInjectStatusForcedRollExtremeIntoCasterCopy(input: Arr, status: Arr, forced: number, env: PortEnv<LuckSkillDeps>): Arr {
    const caster = array(input);
    if (forced === 0) return caster;
    const rt = janusStatusTickForcedExtremeRollType(status, env);
    if (rt === '' || !env.deps.check_uuid(rt)) return caster;
    const layers = array((slot(caster, 'item_layer_types') ?? []) as Arr);
    for (const name of ['roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min']) {
        let rows = slot(layers, name) ?? [];
        if (!isArray(rows)) rows = [rows];
        layers.set(name, array_values(array_filter(rows as Arr, row => closure820RetainTick(row, rt))));
    }
    const name = forced > 0 ? 'roll_type_is_rolled_at_max' : 'roll_type_is_rolled_at_min';
    const rows = array(slot(layers, name) as Arr); rows.append(rt); layers.set(name, rows);
    caster.set('item_layer_types', layers);
    return caster;
}
/** PHP: {closure@820:3246} (includes/functions/janus/core/luck_and_skill_roll_helpers.php:820) */
export function closure820RetainTick(row: unknown, tick: string): boolean {
    return row !== tick && !(isArray(row) && slot(row, 0) === tick);
}
/** PHP: resolve_caster_status_effect_roll_type_result_halved_source (includes/functions/janus/core/luck_and_skill_roll_helpers.php:847) */
export function resolveCasterStatusEffectRollTypeResultHalvedSource(caster: Arr, rt: string, env: PortEnv<LuckSkillDeps>): unknown {
    if (rt === '' || !env.deps.check_uuid(rt)) return null;
    for (const row of values(env.deps.creature_payload_statuses(caster))) {
        if (!isArray(row)) continue;
        const status = slot(row, 'status_arr');
        if (!isArray(status)) continue;
        if (closure853ListContains(env.deps.layer_entries(status, 'roll_type_roll_result_is_halved'), rt, env))
            return env.deps.item_field(row, 'item_name') ?? env.deps.item_field(status, 'item_name') ?? 'Status Effect';
    }
    return closure853ListContains(env.deps.layer_entries(caster, 'roll_type_roll_result_is_halved'), rt, env) ? 'Status Effect' : null;
}
/** PHP: resolve_target_status_effect_roll_type_against_modifiers (includes/functions/janus/core/luck_and_skill_roll_helpers.php:911) */
export function resolveTargetStatusEffectRollTypeAgainstModifiers(target: Arr | null, rt: string, rollBy: string | null = null, env: PortEnv<LuckSkillDeps>): Arr {
    const out = new PhpArray<string, unknown>([['extreme', null], ['double_source', null], ['halve_source', null]]);
    if (!isArray(target) || rt === '' || !env.deps.check_uuid(rt)) return out;
    let max = false, min = false;
    for (const row of values(env.deps.creature_payload_statuses(target))) {
        if (!isArray(row)) continue;
        const status = slot(row, 'status_arr');
        if (!isArray(status)) continue;
        if (env.deps.layer_has(status, 'target_marked_by_caster') && (slot(row, 'status_caster_id') ?? null) !== rollBy) continue;
        const name = env.deps.item_field(row, 'item_name') ?? 'Status Effect';
        if (closure922ListContains(env.deps.layer_entries(status, 'roll_type_against_is_rolled_at_max'), rt, env)) max = true;
        if (closure922ListContains(env.deps.layer_entries(status, 'roll_type_against_is_rolled_at_min'), rt, env)) min = true;
        if (out.get('double_source') === null && closure922ListContains(env.deps.layer_entries(status, 'roll_type_against_roll_result_is_doubled'), rt, env)) out.set('double_source', name);
        if (out.get('halve_source') === null && closure922ListContains(env.deps.layer_entries(status, 'roll_type_against_roll_result_is_halved'), rt, env)) out.set('halve_source', name);
    }
    out.set('extreme', max ? 'max' : min ? 'min' : null);
    return out;
}
/** PHP: resolve_caster_damage_roll_doubling_source_against_target (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1000) */
export function resolveCasterDamageRollDoublingSourceAgainstTarget(caster: Arr, target: Arr | null, rt: string, env: PortEnv<LuckSkillDeps>): unknown {
    if (rt === '' || !env.deps.check_uuid(rt)) return null;
    const type = isArray(target) ? env.deps.item_field(target, 'item_type') ?? env.deps.item_field(slot(target, 'encounter_creatures'), 'item_type') ?? null : null;
    for (const bucket of ['passive_ability', 'feat']) for (let entry of values(env.deps.layer_entries(caster, bucket))) {
        if (!isArray(entry)) continue;
        if (bucket === 'passive_ability' && !env.deps.passive_ability_entry_is_active(entry)) continue;
        entry = env.deps.ft_entry_restore_item_arr(entry);
        let def = slot(entry, 'item_arr') ?? slot(entry, 'item_id') ?? entry;
        if (env.deps.ft_layer_entry_needs_resolve(def)) def = env.deps.ft_resolve_layer_entry(def, caster);
        if (!isArray(def) || env.deps.contains_trigger_key(def)) continue;
        if (!closure1018ListContains(env.deps.layer_entries(def, 'roll_type_roll_result_is_doubled'), rt, env)) continue;
        if (env.deps.layer_has(def, 'ability_works_only_against_item_type')) {
            const allowed = env.deps.layer_entries(def, 'ability_works_only_against_item_type');
            if (type === null || !in_array(type, allowed, true)) continue;
        }
        return env.deps.item_field(def, 'item_name') ?? 'Ability';
    }
    return null;
}
/** PHP: resolve_active_ability_damage_roll_doubling_against_target (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1114) */
export function resolveActiveAbilityDamageRollDoublingAgainstTarget(ability: Arr | null, target: Arr | null, rt: string, env: PortEnv<LuckSkillDeps>): unknown {
    if (rt === '' || !env.deps.check_uuid(rt) || !isArray(ability)) return null;
    if (env.deps.contains_trigger_key(ability)) return null;
    const doubled = env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(ability, 'roll_type_roll_result_is_doubled'));
    if (!in_array(rt, doubled, true)) return null;
    const vitalityIds = sourceCatalogs.VitalityStatus.map(row => row.item_id);
    const has = (id: string): boolean => closure1144TargetHasStatus(target, id, vitalityIds, env);
    if (env.deps.layer_has(ability, 'if_target_has_status_effect_active')) {
        const required = env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(ability, 'if_target_has_status_effect_active'));
        let ok = false;
        for (const id of values(required)) if (has(id as string)) { ok = true; break; }
        if (!ok) return null;
    }
    if (env.deps.layer_has(ability, 'if_target_has_status_effect_not_active')) {
        const forbidden = env.deps.ft_normalize_layer_entries_to_distinct_uuids(env.deps.layer_entries(ability, 'if_target_has_status_effect_not_active'));
        for (const id of values(forbidden)) if (has(id as string)) return null;
    }
    return env.deps.item_field(ability, 'item_name') ?? 'Ability';
}
/** PHP: compute_caster_roll_type_modifiers_signature (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1196) */
export function computeCasterRollTypeModifiersSignature(caster: Arr, resolver: ((...args: unknown[]) => unknown) | null = null, env: PortEnv<LuckSkillDeps>): string {
    const ids = values(env.deps.creature_payload_statuses(caster, resolver)).map(row => slot(row, 'status_id') ?? '');
    return implode('|', sort(ids));
}
/** PHP: caster_skill_roll_modifier_rollup_signature (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1225) */
export function casterSkillRollModifierRollupSignature(caster: Arr, resolver: ((...args: unknown[]) => unknown) | null = null, env: PortEnv<SkillListDeps & Pick<LuckSkillDeps, 'layer_value' | 'layer_entries' | 'creature_payload_statuses' | 'item_field'> & {
    skill_roll_mod_skill_and_modifier(entry: unknown): Arr;
}>): string {
    const modTokens = (entries: Arr): string => closure1227ModTokens(entries, env);
    const parts = [strval(intval(env.deps.layer_value(caster, 'skill_rolls_adv') ?? 0)), strval(intval(env.deps.layer_value(caster, 'skill_rolls_dis') ?? 0))];
    const host = slot(caster, 'item_layer_types');
    for (const key of ['skill_roll_adv', 'skill_roll_dis']) parts.push(skillRollModifierLayerListSignatureSegment(isArray(host) ? slot(host, key) as Arr | null : null, env));
    parts.push(modTokens(isArray(host) && isArray(slot(host, 'skill_roll_mod')) ? slot(host, 'skill_roll_mod') as Arr : []));
    const statuses: string[] = [];
    for (const row of values(env.deps.creature_payload_statuses(caster, resolver))) {
        if (!isArray(row)) continue;
        const view = isArray(slot(row, 'status_arr')) ? slot(row, 'status_arr') as Arr : row;
        let segment = strval(slot(row, 'status_id') ?? env.deps.item_field(view, 'item_id') ?? '');
        for (const key of ['skill_roll_adv', 'skill_roll_dis']) segment += '/' + skillRollModifierLayerListSignatureSegment(env.deps.layer_entries(view, key), env);
        segment += '/' + modTokens(env.deps.layer_entries(view, 'skill_roll_mod'));
        statuses.push(segment);
    }
    parts.push(implode('|', sort(statuses, SORT_STRING)));
    return implode(':', parts);
}
/** PHP: caster_can_read_written_language (includes/functions/janus/core/luck_and_skill_roll_helpers.php:1288) */
export function casterCanReadWrittenLanguage(input: Arr | null, id: string | null, minimum = 2,
    env: PortEnv<Pick<LuckSkillDeps, 'layer_value' | 'build_caster_skills' | 'caster_skill_entry'>>): boolean {
    const normalized = id === null || trim(id) === '' ? null : strtoupper(trim(id));
    let variable: string | null = sourceCatalogs.Language.find(row => strtoupper(row.item_id) === normalized)?.variable_name ?? null;
    if (variable === 'infernal') variable = 'abyssal';
    if (!sourceCatalogs.LanguageSkill.some(row => row.variable_name === variable)) variable = null;
    if (variable === null || input === null || empty(input)) return false;
    if (!empty(env.deps.layer_value(input, 'reads_all_languages'))) return true;
    const caster = { value: phpCopy(input) };
    env.deps.build_caster_skills(caster);
    const skill = env.deps.caster_skill_entry(caster.value, variable);
    return skill !== null && intval(slot(skill, 'tier_level') ?? 0) >= minimum;
}
